/**
 * Bonsai Mail - AI Chat Assistant
 * Natural language interface for email rules, search, and batch filing
 */

import { useEffect, useState, useRef, useCallback } from "react";
import { useBonsai } from "../../contexts/BonsaiContext";
import { errorMessage } from "../../utils/errorMessage";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { 
  Send,
  Sparkles,
  User,
  Loader2,
  Trash2,
  CheckCircle2,
  Clock,
  Mail,
  Search,
  FolderOpen,
  FolderUp,
  Tag,
  AlertTriangle,
  Paperclip
} from "lucide-react";
import { format } from "date-fns";
import { fmtUK } from "../../lib/dateUtils";

export default function BonsaiChat() {
  const { authenticated, user_name, bonsaiApi } = useBonsai();
  
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Search result selection + filing states
  const [selectedSearchIds, setSelectedSearchIds] = useState(new Set());
  const [fileDialogOpen, setFileDialogOpen] = useState(false);
  const [matters, setMatters] = useState([]);
  const [matterSearch, setMatterSearch] = useState("");
  const [selectedMatter, setSelectedMatter] = useState(null);
  const [includeAttachments, setIncludeAttachments] = useState(true);
  const [batchFiling, setBatchFiling] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (authenticated) fetchHistory();
  }, [authenticated]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, sending]);

  const fetchHistory = async () => {
    try {
      const response = await bonsaiApi.get("/chat/history");
      setMessages(response.data);
    } catch (e) {
      console.error("Failed to fetch chat history:", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchMatters = useCallback(async (search = "") => {
    try {
      const params = search ? `?q=${encodeURIComponent(search)}` : "";
      const response = await bonsaiApi.get(`/filing/matters${params}`);
      setMatters(response.data);
    } catch (e) {
      console.error("Failed to fetch matters:", e);
    }
  }, [bonsaiApi]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;

    const userMessage = input.trim();
    setInput("");
    setSending(true);
    setSelectedSearchIds(new Set());

    setMessages(prev => [...prev, {
      id: `temp-${Date.now()}`,
      role: "user",
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);

    try {
      const payload = { message: userMessage };

      // If user has selected search results, include their IDs so the AI can act on them
      if (selectedSearchIds.size > 0) {
        payload.context = {
          selected_message_ids: Array.from(selectedSearchIds),
          selected_count: selectedSearchIds.size,
        };
      }

      const response = await bonsaiApi.post("/chat", payload);

      setMessages(prev => [...prev, {
        id: `resp-${Date.now()}`,
        role: "assistant",
        content: response.data.response,
        actions_taken: response.data.actions_taken,
        search_results: response.data.search_results,
        timestamp: new Date().toISOString()
      }]);

      if (response.data.rules_created?.length > 0) {
        toast.success(`Created ${response.data.rules_created.length} rule(s)`);
      }
    } catch (e) {
      console.error("Failed to send message:", e);
      toast.error("Failed to send message");
      setMessages(prev => prev.filter(m => !m.id.startsWith('temp-')));
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const clearHistory = async () => {
    try {
      await bonsaiApi.delete("/chat/history");
      setMessages([]);
      toast.success("Chat history cleared");
    } catch (e) {
      console.error("Failed to clear history:", e);
      toast.error("Failed to clear history");
    }
  };

  const toggleSearchSelection = (emailId) => {
    setSelectedSearchIds(prev => {
      const next = new Set(prev);
      if (next.has(emailId)) next.delete(emailId);
      else next.add(emailId);
      return next;
    });
  };

  const handleOpenFileDialog = () => {
    setSelectedMatter(null);
    setMatterSearch("");
    setIncludeAttachments(true);
    setBatchProgress(null);
    setFileDialogOpen(true);
    fetchMatters();
  };

  const handleBatchFile = async () => {
    if (selectedSearchIds.size === 0 || !selectedMatter) return;
    const spUrl = selectedMatter.sharePointUrl || selectedMatter.sharepoint_url;
    if (!spUrl) {
      toast.error("Selected matter does not have a SharePoint folder configured");
      return;
    }

    setBatchFiling(true);
    setBatchProgress({ total: selectedSearchIds.size, done: 0, failed: 0 });

    const ids = Array.from(selectedSearchIds);
    let done = 0, failed = 0;

    const results = [];

    for (const emailId of ids) {
      try {
        const response = await bonsaiApi.post("/filing/file", {
          email_id: emailId,
          // PR #466 — explicit-gesture stamp. BonsaiChat's "File these
          // results to <matter>" button confirms the user-chosen
          // matter from the chat search panel. Per the FilingUserIntent
          // vocab, an AI-suggested batch the user has confirmed maps
          // to assist_suggest.
          user_intent: 'assist_suggest',
          sharepoint_path: spUrl,
          matter_id: selectedMatter.id,
          include_attachments: includeAttachments
        });
        if (response.data.success) {
          done++;
          results.push({ emailId, success: true });
        } else {
          failed++;
          results.push({ emailId, success: false, error: response.data.message || "Failed" });
        }
      } catch (e) {
        failed++;
        results.push({ emailId, success: false, error: errorMessage(e, "Failed") });
      }
      setBatchProgress({ total: ids.length, done: done + failed, failed });
    }

    setBatchFiling(false);
    if (failed > 0) {
      const failedMsgs = results.filter(r => !r.success).map(r => r.error || "Unknown error").join("; ");
      toast.error(`${failed} email(s) failed: ${failedMsgs}`, { duration: 8000 });
    }
    if (done > 0) {
      toast.success(`${done}/${ids.length} email(s) filed successfully`);
    }
    setSelectedSearchIds(new Set());
    setFileDialogOpen(false);
    setBatchProgress(null);
  };

  // Get all search result emails from the latest message that has them
  const latestSearchResults = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].search_results?.emails?.length > 0) {
        return messages[i].search_results.emails;
      }
    }
    return [];
  })();

  const quickPrompts = [
    "Find all emails from Edward Money",
    "Search for emails about completion statement",
    "Show me emails from Land Registry",
    "Find emails about 88 Albany"
  ];

  if (!authenticated) {
    return (
      <div className="p-8 text-center">
        <Sparkles className="w-16 h-16 mx-auto mb-4 text-slate-300" />
        <p className="text-slate-500">Please sign in to use the AI assistant</p>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col" data-testid="bonsai-chat-page">
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-6 bg-navy-hover/60">
        {loading ? (
          <div className="space-y-4 max-w-3xl mx-auto">
            {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20" />)}
          </div>
        ) : messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 rounded-md bg-purple-100 flex items-center justify-center mb-4">
              <Sparkles className="w-8 h-8 text-purple-600" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              How can I help you today?
            </h2>
            <p className="text-slate-500 mb-6 max-w-md">
              Search for emails by sender, topic, or date. Select results and file them to a matter, or ask me to create rules.
            </p>
            <div className="flex flex-wrap gap-2 justify-center max-w-lg">
              {quickPrompts.map((prompt, i) => (
                <Button key={i} variant="outline" size="sm" onClick={() => setInput(prompt)} className="text-xs">
                  {prompt}
                </Button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4 max-w-3xl mx-auto">
            {messages.map((message, index) => (
              <div
                key={message.id || index}
                className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {message.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-md bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-4 h-4 text-purple-600" />
                  </div>
                )}
                
                <div className={`max-w-[85%] ${
                  message.role === 'user' 
                    ? 'bg-blue-600 text-white rounded-md rounded-tr-sm' 
                    : 'bg-white border border-navy-border rounded-md rounded-tl-sm'
                } p-4 shadow-sm`}>
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  
                  {/* Actions taken badges */}
                  {message.actions_taken?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-navy-border space-y-2">
                      {message.actions_taken.map((action, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs">
                          {action.type === 'rule_created' && (
                            <>
                              <CheckCircle2 className="w-3 h-3 text-status-green" />
                              <span className="text-slate-600">Created rule: {action.rule_name}</span>
                            </>
                          )}
                          {action.type === 'search_executed' && (
                            <>
                              <Search className="w-3 h-3 text-blue-600" />
                              <span className="text-slate-600">Found {action.result_count} email{action.result_count !== 1 ? 's' : ''}</span>
                            </>
                          )}
                          {action.type === 'search_failed' && (
                            <>
                              <Mail className="w-3 h-3 text-status-red" />
                              <span className="text-status-red">Search failed</span>
                            </>
                          )}
                          {action.type === 'emails_filed' && (
                            <>
                              <FolderOpen className={`w-3 h-3 ${action.filed_count > 0 ? 'text-status-green' : 'text-status-red'}`} />
                              <span className={action.filed_count > 0 ? 'text-status-green' : 'text-status-red'}>
                                {action.filed_count > 0
                                  ? `${action.filed_count} email${action.filed_count !== 1 ? 's' : ''} filed${action.matter_reference ? ` to ${action.matter_reference}` : ''}`
                                  : `Filing failed${action.error ? `: ${action.error}` : ''}`}
                              </span>
                            </>
                          )}
                          {action.type === 'emails_deleted' && (
                            <>
                              <Trash2 className="w-3 h-3 text-status-red" />
                              <span className="text-status-red">{action.deleted_count || 0} email{(action.deleted_count || 0) !== 1 ? 's' : ''} deleted</span>
                            </>
                          )}
                          {action.type === 'emails_moved' && (
                            <>
                              <FolderUp className="w-3 h-3 text-status-amber" />
                              <span className="text-status-amber">{action.moved_count || 0} email{(action.moved_count || 0) !== 1 ? 's' : ''} moved{action.destination ? ` to ${action.destination}` : ''}</span>
                            </>
                          )}
                          {action.type === 'emails_categorized' && (
                            <>
                              <Tag className="w-3 h-3 text-purple-500" />
                              <span className="text-purple-600">{action.categorized_count || 0} categorized as "{action.category}"</span>
                            </>
                          )}
                          {action.type === 'matter_folders_found' && (
                            <>
                              <Search className="w-3 h-3 text-teal" />
                              <span className="text-teal">Found {action.count || 0} matter folder{(action.count || 0) !== 1 ? 's' : ''}</span>
                            </>
                          )}
                          {action.type === 'requires_confirmation' && (
                            <>
                              <AlertTriangle className="w-3 h-3 text-yellow-500" />
                              <span className="text-yellow-600">{action.action_type?.replace("_", " ")} needs confirmation — use the filing sidebar</span>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Selectable search results — email-style cards */}
                  {message.search_results?.emails?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-navy-border" data-testid="search-results">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-txt-muted">
                          {message.search_results.emails.length} results — select to file
                        </span>
                        <button
                          onClick={() => {
                            const allIds = message.search_results.emails.map(e => e.id);
                            const allSelected = allIds.every(id => selectedSearchIds.has(id));
                            if (allSelected) {
                              setSelectedSearchIds(prev => {
                                const next = new Set(prev);
                                allIds.forEach(id => next.delete(id));
                                return next;
                              });
                            } else {
                              setSelectedSearchIds(prev => {
                                const next = new Set(prev);
                                allIds.forEach(id => next.add(id));
                                return next;
                              });
                            }
                          }}
                          className="text-xs text-purple-400 hover:text-purple-300"
                          data-testid="select-all-results"
                        >
                          {message.search_results.emails.every(e => selectedSearchIds.has(e.id)) ? "Deselect all" : "Select all"}
                        </button>
                      </div>
                      <div className="space-y-1 max-h-[400px] overflow-y-auto pr-1">
                        {message.search_results.emails.map((email) => {
                          const senderName = (email.from || "").replace(/<.*>/, "").trim() || email.from;
                          const senderInitials = senderName.split(" ").filter(Boolean).map(w => w[0]).join("").slice(0, 2).toUpperCase();
                          const dateStr = email.date ? new Date(email.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "";
                          const timeStr = email.date ? new Date(email.date).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "";
                          const preview = email.bodyPreview || email.body_preview || email.snippet || "";
                          const isSelected = selectedSearchIds.has(email.id);
                          return (
                            <div
                              key={email.id}
                              className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-all ${
                                isSelected
                                  ? 'border-green-500/60 bg-status-green-dim/20'
                                  : 'border-navy-border hover:border-navy-border bg-navy hover:bg-navy-hover'
                              }`}
                              onClick={() => toggleSearchSelection(email.id)}
                              data-testid={`search-result-${email.id}`}
                            >
                              <div className="pt-0.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => toggleSearchSelection(email.id)}
                                />
                              </div>
                              <div className="w-8 h-8 rounded-full bg-navy-hover border border-navy-border flex items-center justify-center flex-shrink-0">
                                <span className="text-[10px] font-bold text-txt-muted">{senderInitials || "?"}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2 mb-0.5">
                                  <p className="text-xs font-semibold text-white truncate">{senderName}</p>
                                  <span className="text-[10px] text-txt-muted flex-shrink-0 tabular-nums">{dateStr} {timeStr}</span>
                                </div>
                                <p className="text-xs text-txt-muted truncate mb-0.5">{email.subject}</p>
                                {preview && (
                                  <p className="text-[11px] text-txt-muted truncate">{preview.slice(0, 120)}</p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {selectedSearchIds.size > 0 && (
                        <Button
                          onClick={handleOpenFileDialog}
                          size="sm"
                          className="mt-2 w-full gap-2 bg-status-green hover:bg-status-green text-white"
                          data-testid="file-search-results-btn"
                        >
                          <FolderOpen className="w-4 h-4" />
                          File {selectedSearchIds.size} selected email{selectedSearchIds.size !== 1 ? 's' : ''} to matter
                        </Button>
                      )}
                    </div>
                  )}
                  
                  <p className={`text-xs mt-2 ${message.role === 'user' ? 'text-blue-200' : 'text-slate-400'}`}>
                    {fmtUK(message.timestamp, "h:mm a")}
                  </p>
                </div>
                
                {message.role === 'user' && (
                  <div className="w-8 h-8 rounded-md bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-blue-600" />
                  </div>
                )}
              </div>
            ))}
            
            {sending && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-md bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                </div>
                <div className="bg-white border border-navy-border rounded-md rounded-tl-sm p-4 shadow-sm">
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Searching...
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="p-4 border-t border-navy-border bg-navy">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-2 mb-2">
            {selectedSearchIds.size > 0 && (
              <Button
                onClick={handleOpenFileDialog}
                size="sm"
                className="gap-1.5 bg-status-green hover:bg-status-green text-white text-xs"
                data-testid="chat-batch-file-button"
              >
                <FolderOpen className="w-3.5 h-3.5" />
                File {selectedSearchIds.size} email{selectedSearchIds.size !== 1 ? 's' : ''}
              </Button>
            )}
            <div className="flex-1" />
            {messages.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearHistory}
                className="text-txt-muted hover:text-status-red text-xs gap-1"
                data-testid="chat-clear-btn"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Clear
              </Button>
            )}
          </div>
          <form onSubmit={sendMessage} className="flex gap-2">
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={selectedSearchIds.size > 0
                ? `${selectedSearchIds.size} email${selectedSearchIds.size !== 1 ? 's' : ''} selected — type an instruction (file, delete, move...)`
                : "Search emails, file to matters, create rules..."
              }
              className="flex-1 bg-navy-hover border-navy-border text-white placeholder:text-txt-muted"
              disabled={sending}
              data-testid="chat-input"
            />
            <Button 
              type="submit" 
              disabled={!input.trim() || sending}
              className="bg-purple-600 hover:bg-purple-700 px-4"
              data-testid="chat-send"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </form>
        </div>
      </div>

      {/* Batch File Dialog */}
      <Dialog open={fileDialogOpen} onOpenChange={setFileDialogOpen}>
        <DialogContent className="max-w-[680px] w-full">
          <DialogHeader>
            <DialogTitle>File {selectedSearchIds.size} Email{selectedSearchIds.size !== 1 ? 's' : ''} to SharePoint</DialogTitle>
            <DialogDescription>
              Select a CaseMinder matter to file the selected emails to
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Search by client, matter name, or reference"
                value={matterSearch}
                onChange={(e) => {
                  setMatterSearch(e.target.value);
                  fetchMatters(e.target.value);
                }}
                className="pl-10"
                data-testid="chat-file-matter-search"
              />
            </div>
            <div className="max-h-60 overflow-y-auto space-y-2 pr-2">
              {matters.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">
                  {matterSearch ? "No matters found" : "No matters with SharePoint configured"}
                </p>
              ) : (
                matters.map((matter) => (
                  <div
                    key={matter.id}
                    onClick={() => (matter.hasSharePoint || matter.has_sharepoint) && setSelectedMatter(matter)}
                    className={`p-3 rounded-md border transition-colors cursor-pointer ${
                      selectedMatter?.id === matter.id 
                        ? 'border-green-500 bg-status-green-dim' 
                        : (matter.hasSharePoint || matter.has_sharepoint) 
                          ? 'border-navy-border hover:border-green-300 hover:bg-navy-hover/60'
                          : 'border-navy-border bg-navy-hover/60 opacity-50 cursor-not-allowed'
                    }`}
                    data-testid={`chat-matter-option-${matter.reference}`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white">
                          {matter.reference || matter.matter_title || matter.title || "No Reference"}
                        </p>
                        <p className="text-xs text-slate-600">
                          {matter.client || matter.client_name}
                          {(matter.title || matter.matter_title) && matter.reference ? ` - ${matter.title || matter.matter_title}` : ""}
                        </p>
                      </div>
                      {(matter.hasSharePoint || matter.has_sharepoint) ? (
                        <FolderOpen className="w-5 h-5 text-status-green flex-shrink-0" />
                      ) : (
                        <span className="text-xs text-slate-400 flex-shrink-0">No SharePoint</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-navy-hover/60 rounded-md">
              <Checkbox
                id="chat-include-attachments"
                checked={includeAttachments}
                onCheckedChange={setIncludeAttachments}
              />
              <label htmlFor="chat-include-attachments" className="text-sm text-txt flex items-center gap-2">
                <Paperclip className="w-4 h-4" />
                Include attachments
              </label>
            </div>

            {selectedMatter && (
              <div className="p-3 bg-status-green-dim border border-green-200 rounded-md">
                <p className="text-sm text-status-green">
                  <strong>Filing {selectedSearchIds.size} email{selectedSearchIds.size !== 1 ? 's' : ''} to:</strong> {selectedMatter.reference || selectedMatter.matter_title}
                </p>
                <p className="text-xs text-status-green mt-1">
                  Emails → <strong>Attendances</strong> subfolder
                </p>
              </div>
            )}

            {batchProgress && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                <div className="flex items-center justify-between text-sm text-blue-700 mb-2">
                  <span>Filing progress</span>
                  <span>{batchProgress.done}/{batchProgress.total}</span>
                </div>
                <div className="w-full bg-blue-200 rounded-md h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-md transition-all"
                    style={{ width: `${(batchProgress.done / batchProgress.total) * 100}%` }}
                  />
                </div>
                {batchProgress.failed > 0 && (
                  <p className="text-xs text-status-red mt-1">{batchProgress.failed} failed</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFileDialogOpen(false)} disabled={batchFiling}>
              Cancel
            </Button>
            <Button 
              onClick={handleBatchFile} 
              disabled={!selectedMatter || batchFiling}
              className="bg-status-green hover:bg-status-green"
              data-testid="chat-batch-file-submit"
            >
              {batchFiling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FolderOpen className="w-4 h-4 mr-2" />}
              File {selectedSearchIds.size} Email{selectedSearchIds.size !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
